import { InformationCircleIcon, TrashIcon } from '@heroicons/react/20/solid'
import { Pagination, Tooltip } from 'antd'
import { API, Auth } from 'aws-amplify'
import React, { useEffect, useState } from 'react'
import Spin from '../../components/Spin'
import currencies from '../../config/currency'
import i18n from '../../i18n'
import { useAppContext } from '../../libs/context'
import AddMoneyModal from './AddMoneyModal'

const formatDateTime = (date) => {
  if (!date) return ''
  const objectDate = new Date(date)
  if (Number.isNaN(objectDate.getTime())) return ''

  const day = objectDate.getDate()
  const month = objectDate.getMonth() + 1
  const year = objectDate.getFullYear()
  const hours = String(objectDate.getHours()).padStart(2, '0')
  const minutes = String(objectDate.getMinutes()).padStart(2, '0')

  return `${day}/${month}/${year} ${hours}:${minutes}`
}

const TopUps = () => {
  const [topUps, setTopUps] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [topUpsPerPage, setTopUpsPerPage] = useState(10)
  const [userFullInfo, setUserFullInfo] = useState(null)
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [revertingTopUpId, setRevertingTopUpId] = useState(null)
  const { userInfo } = useAppContext()
  const indexOfLast = currentPage * topUpsPerPage
  const indexOfFirst = indexOfLast - topUpsPerPage
  const sortedTopUps = [...topUps].sort((a, b) => {
    const aTime = Date.parse(a.timestamp || a.paymentDate || 0) || 0
    const bTime = Date.parse(b.timestamp || b.paymentDate || 0) || 0
    return bTime - aTime
  })

  const getTopUps = async () => {
    setLoading(true)
    try {
      const tokenReq = (await Auth.currentAuthenticatedUser()).signInUserSession
        .idToken.jwtToken
      const result = await API.get('rcs-api', '/topups', {
        headers: {
          Authorization: tokenReq
        }
      })
      setTopUps(result)
    } catch (err) {
      console.log('error con los toputps')
    }
    setLoading(false)
  }

  const handleRevertTopUp = async (topUp) => {
    if (!topUp?.userId || !topUp?.timestamp) return
    const shouldRevert = window.confirm(
      '¿Revertir este top up? Esta acción no se puede deshacer.'
    )
    if (!shouldRevert) return

    setRevertingTopUpId(topUp.topUpId || topUp.timestamp)
    try {
      const tokenReq = (await Auth.currentAuthenticatedUser()).signInUserSession
        .idToken.jwtToken
      await API.post('rcs-api', '/topups/revert', {
        headers: {
          Authorization: tokenReq
        },
        body: {
          userId: topUp.userId,
          timestamp: topUp.timestamp
        }
      })
      await getTopUps()
    } catch (err) {
      console.log(err)
      window.alert('No se pudo revertir el top up. Revisa la consola.')
    } finally {
      setRevertingTopUpId(null)
    }
  }

  const handleAddMoney = async () => {
    try {
      const tokenReq = (await Auth.currentAuthenticatedUser()).signInUserSession
        .idToken.jwtToken
      if (userInfo?.isAdmin) {
        const usersResp = await API.get('rcs-api', '/users', {
          headers: {
            Authorization: tokenReq
          }
        })
        setUsers(usersResp || [])
        const currentUserId = userInfo?.originalUserId || userInfo?.userId
        const defaultUserId =
          usersResp?.find((user) => user.userId === currentUserId)?.userId ||
          usersResp?.[0]?.userId ||
          ''
        setSelectedUserId(defaultUserId)
        setOpen(true)
      } else {
        const resp = await API.get('rcs-api', '/user', {
          headers: {
            Authorization: tokenReq
          }
        })
        setUserFullInfo(resp)
        setOpen(true)
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    getTopUps()
  }, [])

  return (
    <>
      {loading ? (
        <div className="flex justify-center my-16">
          <Spin type="lightBg" />
        </div>
      ) : (
        <div>
          {((userInfo?.isAdmin && users.length) || userFullInfo) && (
            <AddMoneyModal
              open={open}
              client={
                userInfo?.isAdmin
                  ? users.find((user) => user.userId === selectedUserId)
                  : userFullInfo
              }
              users={users}
              selectedUserId={selectedUserId}
              setSelectedUserId={setSelectedUserId}
              setOpen={setOpen}
              setTopUps={setTopUps}
              userInfo={userInfo}
              onTopUpSuccess={getTopUps}
            />
          )}
          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <button
                  type="button"
                  className={
                    'bg-blue-dark hover:bg-indigo-700 bg-gray-300 ml-auto inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark'
                  }
                  onClick={() => handleAddMoney()}
                >
                  {i18n.t('add_money')}
                </button>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mt-5">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        {userInfo.isAdmin && (
                          <th
                            scope="col"
                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          >
                            Client
                          </th>
                        )}
                        {userInfo.isAdmin && (
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            Top Up Id
                          </th>
                        )}
                        {userInfo.isAdmin && (
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            Recargado por
                          </th>
                        )}
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {i18n.t('concept')}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {i18n.t('amount')}
                        </th>
                        <th
                          scope="col"
                          className="pl-3 pr-4 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {i18n.t('date')}
                        </th>
                        {userInfo.isAdmin && (
                          <th
                            scope="col"
                            className="pl-3 pr-4 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            Acciones
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {sortedTopUps
                        .slice(indexOfFirst, indexOfLast)
                        .map((topUp, topUpIdx) => (
                          <tr
                            key={topUp.topUpId}
                            className={
                              topUpIdx % 2 === 0 ? undefined : 'bg-gray-50'
                            }
                          >
                            {userInfo.isAdmin && (
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {topUp.username
                                  ? topUp.username
                                  : topUp.email || 'No asignado'}
                              </td>
                            )}
                            {userInfo.isAdmin && (
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <Tooltip
                                  placement="right"
                                  title={`${topUp.topUpId}`}
                                >
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                  >
                                    <InformationCircleIcon className="h-6 w-6" />
                                  </button>
                                </Tooltip>
                              </td>
                            )}
                            {userInfo.isAdmin && (
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {topUp.createdByEmail ||
                                  topUp.createdByUserId ||
                                  '-'}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {topUp.concept}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {topUp.amount +
                                ' ' +
                                currencies[topUp.currency].symbol}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDateTime(
                                topUp.timestamp || topUp.paymentDate
                              )}
                            </td>
                            {userInfo.isAdmin && (
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {topUp.source === 'manual' &&
                                !topUp.reverted ? (
                                  <Tooltip
                                    placement="left"
                                    title="Revertir top up"
                                  >
                                    <button
                                      type="button"
                                      className="inline-flex items-center text-red-600 hover:text-red-700 disabled:opacity-50"
                                      onClick={() => handleRevertTopUp(topUp)}
                                      disabled={
                                        revertingTopUpId ===
                                        (topUp.topUpId || topUp.timestamp)
                                      }
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  </Tooltip>
                                ) : topUp.reverted ? (
                                  'Revertido'
                                ) : (
                                  '-'
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <Pagination
            className="mt-3"
            total={sortedTopUps.length}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            defaultPageSize={topUpsPerPage}
            defaultCurrent={1}
            current={currentPage}
            onChange={setCurrentPage}
            onShowSizeChange={(current, size) => setTopUpsPerPage(size)}
          />
        </div>
      )}
    </>
  )
}
export default TopUps
